/**
 * FuzzySearch + FuzzySearchInput — Motor y UI de búsqueda difusa.
 *
 * Motor: usa uFuzzy (MIT, ~7KB) para búsqueda por subsecuencia con:
 *   - Tolerancia a typos (SingleError mode)
 *   - Scoring por proximidad de caracteres (gaps penalizados)
 *   - Highlight ranges nativos (posiciones exactas del match)
 *   - Performance: sin índice, O(n) sobre haystack, ~1ms para 10K items
 *
 * UI: FuzzySearchInput genera input + dropdown con highlights.
 *   Reutilizable en cualquier módulo del admin.
 *
 * @see https://github.com/leeoniya/uFuzzy
 */

import uFuzzy from './lib/uFuzzy.js';
import { escapeHtmlParaInyeccionSegura } from './utils/escape-html-para-inyeccion-segura.js';
import { t } from './services/i18n-admin.js';

// ============================================================================
// FuzzySearch — Motor de búsqueda (sin UI)
// ============================================================================

class FuzzySearch {
    /**
     * @param {Array} items — array de objetos a buscar
     * @param {Array<string>} searchableFields — campos donde buscar
     * @param {Object} [options]
     * @param {number} [options.minQueryLength=1] — mínimo de caracteres para buscar
     * @param {number} [options.maxResults=50] — máximo de resultados
     */
    constructor(items, searchableFields, options = {}) {
        this.searchableFields = searchableFields || [];
        this.options = {
            minQueryLength: options.minQueryLength || 1,
            maxResults: options.maxResults || 50,
        };

        // uFuzzy con typo tolerance y scoring por proximidad
        this._uf = new uFuzzy({
            intraMode: 1,   // permite caracteres extra entre los del query
            intraIns: 1,    // máximo 1 inserción intra-término (typos)
        });

        this.items = [];
        this._haystack = [];  // array de strings concatenados para uFuzzy
        if (items && items.length > 0) {
            this.setItems(items);
        }
    }

    /**
     * Actualiza los items del motor.
     * @param {Array} items
     */
    setItems(items) {
        this.items = items || [];
        // Construir haystack: concatenar campos buscables de cada item en un string
        this._haystack = [];
        for (let i = 0; i < this.items.length; i++) {
            const parts = [];
            for (let j = 0; j < this.searchableFields.length; j++) {
                const val = this._getNestedValue(this.items[i], this.searchableFields[j]);
                if (val != null) parts.push(String(val));
            }
            this._haystack.push(parts.join(' '));
        }
    }

    /**
     * Busca items que coincidan con la query.
     * @param {string} query
     * @returns {Array<{item: Object, score: number, highlights: Object}>}
     */
    search(query) {
        if (!query || query.length < this.options.minQueryLength) {
            return this.items.map(function (item) { return { item: item, score: 0, highlights: {} }; });
        }

        // uFuzzy: buscar en haystack
        const idxs = this._uf.filter(this._haystack, query);
        if (!idxs || idxs.length === 0) return [];

        const info = this._uf.info(idxs, this._haystack, query);
        const order = this._uf.sort(info, this._haystack, query);

        const results = [];
        const max = Math.min(order.length, this.options.maxResults);
        for (let i = 0; i < max; i++) {
            const haystackIndex = info.idx[order[i]];
            const item = this.items[haystackIndex];
            const ranges = info.ranges[order[i]];

            // Generar highlights por campo
            const highlights = this._generarHighlightsPorCampo(item, query, this._haystack[haystackIndex], ranges);

            results.push({
                item: item,
                score: max - i,  // mayor score = mejor match
                highlights: highlights,
            });
        }

        return results;
    }

    /**
     * Genera highlights para cada campo buscable del item.
     * Usa las posiciones de uFuzzy para marcar con <mark>.
     */
    _generarHighlightsPorCampo(item, query, haystackStr, ranges) {
        const highlights = {};

        // Aplicar highlight al haystack completo
        const highlightedHaystack = this._aplicarRangesComoMark(haystackStr, ranges);

        // Para cada campo, buscar su valor en el haystack y extraer el highlight
        for (let i = 0; i < this.searchableFields.length; i++) {
            const field = this.searchableFields[i];
            const val = this._getNestedValue(item, field);
            if (val == null) continue;

            const strVal = String(val);

            // Intentar highlight individual por campo (más preciso)
            const fieldIdxs = this._uf.filter([strVal], query);
            if (fieldIdxs && fieldIdxs.length > 0) {
                const fieldInfo = this._uf.info(fieldIdxs, [strVal], query);
                if (fieldInfo.ranges[0]) {
                    highlights[field] = this._aplicarRangesComoMark(strVal, fieldInfo.ranges[0]);
                    continue;
                }
            }

            // Fallback: escapar sin highlight
            highlights[field] = escapeHtmlParaInyeccionSegura(strVal);
        }

        return highlights;
    }

    /**
     * Aplica ranges de uFuzzy como etiquetas <mark> en el texto.
     * ranges es un array de pares [start, end, start, end, ...]
     */
    _aplicarRangesComoMark(texto, ranges) {
        if (!ranges || ranges.length === 0) return escapeHtmlParaInyeccionSegura(texto);

        let resultado = '';
        let ultimaPosicion = 0;

        for (let i = 0; i < ranges.length; i += 2) {
            const inicio = ranges[i];
            const fin = ranges[i + 1];

            // Texto antes del match
            resultado += escapeHtmlParaInyeccionSegura(texto.substring(ultimaPosicion, inicio));
            // Texto del match
            resultado += '<mark>' + escapeHtmlParaInyeccionSegura(texto.substring(inicio, fin)) + '</mark>';
            ultimaPosicion = fin;
        }

        // Texto después del último match
        resultado += escapeHtmlParaInyeccionSegura(texto.substring(ultimaPosicion));

        return resultado;
    }

    /**
     * Obtiene valor de un campo anidado (soporta 'a.b.c').
     */
    _getNestedValue(obj, field) {
        const parts = field.split('.');
        let current = obj;
        for (let i = 0; i < parts.length; i++) {
            if (current == null) return null;
            current = current[parts[i]];
        }
        return current;
    }

}

// ============================================================================
// FuzzySearchInput — Componente UI (input + dropdown con highlights)
// ============================================================================

class FuzzySearchInput {
    /**
     * @param {Object} config
     * @param {HTMLElement} config.container — dónde renderizar el input
     * @param {Array} config.items — array de objetos
     * @param {string[]} config.searchFields — campos para buscar
     * @param {string} config.displayField — campo a mostrar en sugerencias
     * @param {string} [config.placeholder='Buscar...']
     * @param {Function} [config.onSelect] — callback al seleccionar (item)
     * @param {Function} [config.onClear] — callback al borrar todo
     * @param {Function} [config.renderItem] — (item, highlights) => HTML
     * @param {boolean} [config.allowCreate=false] — mostrar "Crear nuevo"
     * @param {Function} [config.onCreate] — callback al crear
     */
    constructor(config) {
        this.config = {
            placeholder: 'Buscar...',
            allowCreate: false,
            renderItem: null,
            ...config,
        };

        this.searcher = new FuzzySearch(
            config.items || [],
            config.searchFields || [],
            { maxResults: 20 }
        );

        this.selectedItem = null;
        this.highlightedIndex = -1;
        this._results = [];

        this._render();
        this._bindEvents();
    }

    setItems(items) {
        this.searcher.setItems(items);
    }

    setSelectedItem(item) {
        this.selectedItem = item;
        if (item && this.config.displayField) {
            this.input.value = item[this.config.displayField] || '';
        }
    }

    // ========================================================================
    // RENDER
    // ========================================================================

    _render() {
        const container = this.config.container;
        if (!container) return;

        // Input
        this.input = document.createElement('input');
        this.input.type = 'text';
        this.input.className = 'form-input fuzzy-search-input';
        this.input.placeholder = this.config.placeholder;
        this.input.autocomplete = 'off';

        // Dropdown
        this.dropdown = document.createElement('div');
        this.dropdown.className = 'fuzzy-search-dropdown';
        this.dropdown.style.display = 'none';

        container.appendChild(this.input);
        container.appendChild(this.dropdown);

        // Estilos (inyectar una sola vez)
        if (!document.getElementById('fuzzy-search-styles')) {
            const style = document.createElement('style');
            style.id = 'fuzzy-search-styles';
            style.textContent = `
                .fuzzy-search-dropdown {
                    position: fixed;
                    background: var(--bg-card, #1e293b);
                    border: 1px solid var(--border, #334155);
                    border-radius: 8px;
                    max-height: 280px;
                    overflow-y: auto;
                    z-index: 9999;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.4);
                }
                .fuzzy-search-item {
                    padding: 8px 12px;
                    cursor: pointer;
                    font-size: 0.85rem;
                    color: var(--text, #e2e8f0);
                    border-bottom: 1px solid var(--border, #334155);
                }
                .fuzzy-search-item:last-child { border-bottom: none; }
                .fuzzy-search-item:hover, .fuzzy-search-item.highlighted {
                    background: var(--surface-hover, rgba(255,255,255,0.08));
                }
                .fuzzy-search-item mark {
                    background: transparent;
                    color: var(--accent-blue, #60a5fa);
                    font-weight: 700;
                    text-decoration: underline;
                }
                .fuzzy-search-create {
                    padding: 8px 12px;
                    cursor: pointer;
                    font-size: 0.85rem;
                    color: var(--accent-blue, #60a5fa);
                    font-weight: 600;
                    border-top: 1px solid var(--border, #334155);
                }
                .fuzzy-search-create:hover { background: var(--surface-hover, rgba(255,255,255,0.08)); }
            `;
            document.head.appendChild(style);
        }
    }

    // ========================================================================
    // EVENTS
    // ========================================================================

    _bindEvents() {
        const self = this;

        this.input.addEventListener('input', function () {
            const query = self.input.value.trim();
            if (!query) {
                self._hideDropdown();
                self.selectedItem = null;
                if (self.config.onClear) self.config.onClear();
                return;
            }

            const results = self.searcher.search(query);
            self._results = results;
            self._showResults(results);
        });

        this.input.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                self._moveHighlight(1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                self._moveHighlight(-1);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (self.highlightedIndex >= 0 && self._results[self.highlightedIndex]) {
                    // Ya hay uno resaltado (por flechas o por el 1er Enter) → seleccionarlo.
                    self._selectResult(self._results[self.highlightedIndex]);
                } else if (self._results && self._results.length) {
                    // 1er Enter sin nada resaltado: resaltar el PRIMERO (el mejor match) sin seleccionar — así lo
                    // ves marcado y un 2do Enter lo activa. Más reactivo y predecible que elegir a ciegas.
                    // _moveHighlight(1) desde highlightedIndex=-1 lleva al índice 0 (igual que ArrowDown).
                    self._moveHighlight(1);
                }
            } else if (e.key === 'Escape') {
                self._hideDropdown();
            }
        });

        // Cerrar al click fuera
        document.addEventListener('click', function (e) {
            if (!self.input.contains(e.target) && !self.dropdown.contains(e.target)) {
                self._hideDropdown();
            }
        });
    }

    // ========================================================================
    // DROPDOWN
    // ========================================================================

    _showResults(results) {
        if (results.length === 0) {
            this.dropdown.innerHTML = `<div class="fuzzy-search-item" style="color:var(--text-secondary)">${t('Sin resultados')}</div>`;
            this._positionDropdown();
            this.dropdown.style.display = '';
            return;
        }

        let html = '';
        for (let i = 0; i < results.length; i++) {
            const r = results[i];
            const displayField = this.config.displayField;

            let itemHtml;
            if (this.config.renderItem) {
                itemHtml = this.config.renderItem(r.item, r.highlights);
            } else {
                itemHtml = r.highlights[displayField] || escapeHtmlParaInyeccionSegura(String(r.item[displayField] || ''));
            }

            html += '<div class="fuzzy-search-item" data-index="' + i + '">' + itemHtml + '</div>';
        }

        if (this.config.allowCreate) {
            html += `<div class="fuzzy-search-create" data-create="true">${t('+ Crear nuevo')}</div>`;
        }

        this.dropdown.innerHTML = html;
        this.highlightedIndex = -1;

        // Click en items
        const self = this;
        const items = this.dropdown.querySelectorAll('.fuzzy-search-item');
        for (let i = 0; i < items.length; i++) {
            items[i].addEventListener('click', function () {
                const idx = parseInt(this.dataset.index);
                if (!isNaN(idx) && results[idx]) {
                    self._selectResult(results[idx]);
                }
            });
        }

        const createBtn = this.dropdown.querySelector('.fuzzy-search-create');
        if (createBtn) {
            createBtn.addEventListener('click', function () {
                self._hideDropdown();
                if (self.config.onCreate) self.config.onCreate(self.input.value.trim());
            });
        }

        this._positionDropdown();
        this.dropdown.style.display = '';
    }

    _selectResult(result) {
        this.selectedItem = result.item;
        const displayField = this.config.displayField;
        this.input.value = result.item[displayField] || '';
        this._hideDropdown();
        if (this.config.onSelect) this.config.onSelect(result.item);
    }

    _hideDropdown() {
        this.dropdown.style.display = 'none';
        this.highlightedIndex = -1;
    }

    _positionDropdown() {
        const rect = this.input.getBoundingClientRect();
        this.dropdown.style.top = (rect.bottom + 2) + 'px';
        this.dropdown.style.left = rect.left + 'px';
        this.dropdown.style.width = rect.width + 'px';
    }

    _moveHighlight(direction) {
        const items = this.dropdown.querySelectorAll('.fuzzy-search-item');
        if (items.length === 0) return;

        // Quitar highlight actual
        if (this.highlightedIndex >= 0 && items[this.highlightedIndex]) {
            items[this.highlightedIndex].classList.remove('highlighted');
        }

        this.highlightedIndex += direction;
        if (this.highlightedIndex < 0) this.highlightedIndex = items.length - 1;
        if (this.highlightedIndex >= items.length) this.highlightedIndex = 0;

        items[this.highlightedIndex].classList.add('highlighted');
        items[this.highlightedIndex].scrollIntoView({ block: 'nearest' });
    }

    /** Limpiar DOM y estado */
    destroy() {
        if (this.dropdown) this.dropdown.remove();
        if (this.container) this.container.innerHTML = '';
        this._selectedItem = null;
        this.highlightedIndex = -1;
    }
}

// ============================================================================
// EXPORTS
// ============================================================================

export { FuzzySearch, FuzzySearchInput };
